<template>
  <div>
    <sdui-survey-badges
      v-if="!isAuthor || !isPreviewMode"
      :results-visibility="survey.results_visibility"
      :is-anonymous="survey.is_anonymous"
    />

    <VForm id="form-multi-question-survey-options-overview"
           ref="form"
           @submit="submitVotes"
    >
      <fieldset
        v-for="(question, index) in survey.questions"
        :key="question.id"
        :data-cy="`multi-question-survey-question-${index}`"
        class="mt-4"
        :class="{ 'pt-2': index !== 0 }"
      >
        <legend
          class="h4 font-weight-bold gray-400 fz-base"
          :class="isQuestionTypeFreetext(question) && !isPreviewMode ? 'mb-0' : 'mb-2'"
          data-test="survey-question"
        >
          {{ index + 1 }}: {{ question.question }}*
        </legend>

        <!-- EMPTY STATE (freetext question and question with user option only)-->
        <div
          v-if="!question.options.length && isPreviewMode"
          class="text-center text-muted small py-4"
          data-test="survey-option-empty-state"
        >
          {{ $t('survey.no_answers_yet') }}
        </div>

        <div v-else-if="isQuestionTypeFreetext(question) && !isPreviewMode">
          <sdui-survey-free-text-voting
            :survey-visibility="survey.results_visibility"
            :user-has-voted="survey.is_user_voted"
            :question-index="index + 1"
            :question="question"
            data-test="survey-free-text-voting"
            @update="(value) => handleNewFreeTextInput(value, getQuestionById(question.id))"
          />
        </div>
        <div v-else>
          <!-- in previewMode, we need to hide the empty user/freetext survey-option if the user has not voted yet,
          to avoid showing an empty option -->
          <template v-for="option in getQuestionById(question.id).options" :key="option.uuid">
            <sdui-survey-option
              v-if="!isPreviewMode || (!option.has_user_option && option.name)"
              :key="option.uuid"
              :radio-btn-group-prefix="question.id"
              :name="option.id"
              :disabled="isPreviewMode"
              :option="option"
              :show-results="showResults"
              :is-survey-public-release="survey.results_visibility !== resultsVisibilityOptions.NONE"
              :is-question-type-free-text="isQuestionTypeFreetext(question)"
              :model-value="getMultiQuestionSurveyOptionValue(question.uuid)"
              :is-multiple-answerable="isMultiAnswerableQuestion(question)"
              :input-prefix="inputPrefix + isPreviewMode ? '-preview' : ''"
              :data-cy="option.has_user_option ? 'sdui-survey-single-question-user-option' : 'sdui-survey-single-question-option'"
              @change="(uuid) => multiQuestionOptionChanged(question, uuid)"
              @validate-option="validateFreetextOption"
              @cancel-option="(cancelledOption) => cancelUserOption(cancelledOption, question)"
            />
          </template>
        </div>
      </fieldset>

      <!-- submit button in modal footer -->
      <SModalPortalTo v-if="!isPreviewMode">
        <b-button
          v-btn-focus-restore-after-disable
          block
          size="lg"
          type="submit"
          form="form-multi-question-survey-options-overview"
          :class="$wait.waiting(`voting-${survey.id}`) ? 'spinner-btn' : 'no-spinner'"
          :variant="isSubmitMultiQuestionBtnEnabled ? 'primary' : 'secondary'"
          :disabled="!isSubmitMultiQuestionBtnEnabled || $wait.waiting(`voting-${survey.id}`)"
          data-cy="sdui-multi-question-survey-submit"
        >
          {{ $t('survey.voting.vote_btn') }}
        </b-button>
      </SModalPortalTo>
    </VForm>
  </div>
</template>
<script>
import SduiSurveyBadges from 'src/components/miscellaneous/ui/surveys/SduiSurveyBadges.vue'
import SduiSurveyOption from 'src/components/miscellaneous/ui/surveys/SduiSurveyOption.vue'
import surveyMixin from 'src/mixins/channels/survey/surveyMixin.js'
import constants from 'src/config/constants.ts'
import SduiSurveyFreeTextVoting from 'src/components/miscellaneous/ui/surveys/SduiSurveyFreeTextVoting.vue'
import SModalPortalTo from 'src/components/base/SModal/SModalPortalTo.vue'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'

export default {
  name: 'SduiMultiQuestionSurveyOptionsOverview',
  components: { SModalPortalTo, SduiSurveyFreeTextVoting, SduiSurveyBadges, SduiSurveyOption },
  mixins: [surveyMixin],
  props: {
    survey: {
      type: Object,
      required: true,
    },

    inputPrefix: {
      type: String,
      default: 'option',
    },

    isAuthor: {
      type: Boolean,
      default: false,
    },

    isPreviewMode: {
      type: Boolean,
      default: false,
    },

    showResults: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    resultsVisibilityOptions: constants.survey.resultsVisibility,
    multiQuestionSelectedValue: {},
  }),

  computed: {
    freeTextQuestionUuids() {
      return this.survey.questions.filter(this.isQuestionTypeFreetext).map((question) => question.uuid)
    },

    alreadyVoted() {
      return this.survey.is_user_voted
    },

    multiQuestionOptions() {
      return this.survey.questions.map((question) => {
        if ((question.has_user_option || this.isQuestionTypeFreetext(question)) && !this.alreadyVoted) {
          return {
            uuid: question.uuid,
            type: question.type,
            questionId: question.id,
            options: [...question.options, question.has_user_option ? this.createNewUserOption() : this.createNewFreetextOption()],
          }
        }
        return {
          questionId: question.id,
          options: question.options,
        }
      })
    },

    isSubmitMultiQuestionBtnEnabled() {
      return Object.keys(this.multiQuestionSelectedValue).length === this.survey.questions.length
    },

    freeTextOptionUuids() {
      const freeTextUUIDs = []
      Object.values(this.multiQuestionOptions).forEach((question) => {
        question.options.forEach((option) => {
          if (option.has_user_option || this.isQuestionTypeFreetext(question)) {
            freeTextUUIDs.push(option.uuid)
          }
        })
      })
      return freeTextUUIDs
    },
  },

  emits: ['closeAndUpdate'],

  methods: {
    getQuestionById(questionId) {
      return this.multiQuestionOptions.find((mQuestionOpt) => mQuestionOpt.questionId === questionId)
    },

    handleNewFreeTextInput(value, question) {
      const freetextOption = question.options.find((option) => option.is_freetext_question_option)

      this.multiQuestionOptionChanged(question, freetextOption.uuid)
      this.validateFreetextOption({ answer: value }, freetextOption)
    },

    multiQuestionOptionChanged(question, optionUuid, preventUserOptionDeselection = false) {
      this.multiQuestionSelectedValue = this.questionOptionChanged(
        this.multiQuestionSelectedValue,
        question,
        optionUuid,
        preventUserOptionDeselection,
        this.isQuestionTypeFreetext(question),
      )
    },

    async submitVotes() {
      const data = await this.submitAnswer(
        this.survey.id,
        this.multiQuestionSelectedValue,
        this.freeTextOptionUuids,
        this.survey.questions,
      )
      clyTracking.trackSurvey().action.submit({ questionCount: this.survey.questions.length })
      if (!data) return
      this.$emit('closeAndUpdate', data)
    },

    validateFreetextOption({ answer }, { uuid: freetextOptionUuid }) {
      if (!answer) return
      for (const questionObject of Object.values(this.multiQuestionSelectedValue)) {
        if (questionObject.options.includes(freetextOptionUuid)) {
          questionObject.new_option = answer
          return
        }
      }
    },

    getMultiQuestionSurveyOptionValue(questionUuid) {
      return this.multiQuestionSelectedValue[questionUuid]?.options
    },
  },
}
</script>
